import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiToast } from '@hooks/useApiToast';
import { enterpriseApiClient } from '@api/enterprise/client';
import { WorkOrdersApi } from '@api/enterprise/openapi';
import type {
  WorkOrderViewModel,
  UpdateWorkOrderCommand
} from '@api/enterprise/openapi/models';
import { workOrderKeys } from '../constants';

type UpdateWorkOrderParams = {
  jobNumber: string;
  command: UpdateWorkOrderCommand;
};

type UseUpdateWorkOrderOptions = {
  onSuccess?: () => void;
  showSuccessToast?: boolean;
};

export const useUpdateWorkOrder = (options: UseUpdateWorkOrderOptions = {}) => {
  const { onSuccess: onSuccessCallback, showSuccessToast = true } = options;
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<WorkOrderViewModel, Error, UpdateWorkOrderParams>({
    mutationFn: async ({ jobNumber, command }: UpdateWorkOrderParams) => {
      const api = new WorkOrdersApi(undefined, undefined, enterpriseApiClient);
      const response = await api.apiWorkOrdersJobNumberPut({
        jobNumber,
        updateWorkOrderCommand: command
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.byJobNumber(variables.jobNumber)
      });
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.odata()
      });

      if (showSuccessToast) {
        showSuccess('updateWorkOrder.success', 'workOrders');
      }
      onSuccessCallback?.();
    },
    onError: (error, _variables) => {
      showError('updateWorkOrder.error', error, 'workOrders');
    }
  });
};
